import type { Translate } from 'next-translate';
import { match } from 'ts-pattern';

export type EntryNameKind = 'real' | 'group' | 'partial';

export type EntryNameSource = {
  entryName?: string;
  entryOrder?: number;
  isMultiOwner?: boolean;
  isPickController?: boolean;
  isEntryOriginator?: boolean;
  isViewerEntry?: boolean;
  isPartialEntry?: boolean;
};

export type EntryNameDisplay = {
  entryName: string;
  entryOrder?: number;
  nameKind: EntryNameKind;
  isNameGenerated: boolean;
  canEditName: boolean;
};

const resolveNameKind = ({
  isMultiOwner,
  isPickController,
  isEntryOriginator,
  isViewerEntry,
  isPartialEntry,
}: EntryNameSource): EntryNameKind => {
  if (!isViewerEntry || !isMultiOwner || isPickController || isEntryOriginator) {
    return 'real';
  }

  return isPartialEntry ? 'partial' : 'group';
};

export const resolveEntryNameDisplay = (source: EntryNameSource): EntryNameDisplay => {
  const nameKind = resolveNameKind(source);
  const isNameGenerated = nameKind !== 'real';

  return {
    entryName: source.entryName ?? '',
    entryOrder: isNameGenerated ? undefined : source.entryOrder,
    nameKind,
    isNameGenerated,
    canEditName: !!source.isViewerEntry && (!source.isMultiOwner || !!source.isPickController),
  };
};

export const resolveEntryNameLabel = (
  { entryName, nameKind }: Pick<EntryNameDisplay, 'entryName' | 'nameKind'>,
  t: Translate,
  pickControllerHandle?: string
): string =>
  match(nameKind)
    .with('real', () => entryName)
    .with('partial', () => t('gamecenter.common.entryName.partialEntry'))
    .with('group', () =>
      pickControllerHandle
        ? t('gamecenter.common.entryName.groupEntryWith', { handle: pickControllerHandle })
        : t('gamecenter.common.entryName.groupEntry')
    )
    .exhaustive();
